import InfoIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  BenefitClass,
  CensusDropzone,
  CensusError,
  CensusRadio,
  CensusTitle,
  Coverage,
  COVERAGE_LABELS,
  CoverageColumn,
  downloadTemplate,
  FormState,
  LOADING,
  YES,
  YES_NO_OPTIONS,
} from "@platform/census-components";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import set from "lodash/set";
import { PropsWithChildren, useMemo } from "react";
import {
  Link,
  TextField,
  Typography,
  type TypographyProps,
} from "@platform/design-system";

interface Props {
  formState: FormState;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
}

const CensusTabCensusForm = ({
  formState,
  updateFormStateValue,
  onChange,
}: Props): React.JSX.Element => {
  const isLoading = useMemo(
    () => formState.state === LOADING,
    [formState.state],
  );

  return (
    <>
      <CensusTitle title="Company Details" />
      <Box px={2} mb={3}>
        <CensusRadio
          name="fullTimeEmployees"
          label="Are all employees on this census considered full-time employees?"
          onChange={onChange}
          options={YES_NO_OPTIONS}
          value={formState.fullTimeEmployees}
          disabled={isLoading}
        />
      </Box>

      <ClassSection
        isLoading={isLoading}
        formState={formState}
        updateFormStateValue={updateFormStateValue}
        onChange={onChange}
      />

      <MultipleOptionSection
        isLoading={isLoading}
        formState={formState}
        updateFormStateValue={updateFormStateValue}
      />

      <CensusTitle title="Census File" mt={3} />
      <Box px={2} mb={3} mt={3}>
        <FormControl>
          <FormLabel required>
            Which row number has your column headers?
          </FormLabel>
          <TextField
            name="row"
            sx={{ marginTop: 1 }}
            label="Headers Row Number"
            type="number"
            required
            value={formState.row}
            onChange={onChange}
            disabled={isLoading}
          />
        </FormControl>
      </Box>

      <CensusTitle title="Upload Census" />
      <Typography variant="body-large">
        Don&apos;t have the template?{" "}
        <Link component="button" underline="none" onClick={downloadTemplate}>
          <strong>Click here to download.</strong>
        </Link>
      </Typography>
      {formState.censusError && formState.censusFile ? (
        <CensusError
          censusError={formState.censusError}
          file={formState.censusFile}
        />
      ) : (
        <CensusDropzone
          file={formState.censusFile}
          fileError={formState.fileError}
          onChange={(file) => updateFormStateValue("censusFile", file)}
          onFileError={(error) => updateFormStateValue("fileError", error)}
        />
      )}
    </>
  );
};

interface PropsWithLoading extends Props {
  isLoading: boolean;
}

function ClassSection({
  formState,
  onChange,
  isLoading,
  updateFormStateValue,
}: PropsWithLoading): React.JSX.Element {
  const handleColumnChange = (
    coverage: CoverageColumn,
    value: string,
  ): void => {
    const newCoveragesColumns = formState.coveragesColumns.map((c) =>
      c.coverageType === coverage.coverageType
        ? { ...c, columnLetter: value }
        : c,
    );

    // sync class names for the changed column
    const filteredCoveragesColumns = newCoveragesColumns.filter(
      (c) => c.columnLetter === value,
    );
    const classWithNewValueMap = new Map<string, BenefitClass>();
    filteredCoveragesColumns.forEach((c) => {
      c.classes.forEach((bc) => {
        const savedClass = classWithNewValueMap.get(bc.id);
        if (!savedClass || !savedClass.newName) {
          classWithNewValueMap.set(bc.id, bc);
        }
      });
    });

    updateFormStateValue(
      "coveragesColumns",
      newCoveragesColumns.map((c) => {
        if (c.columnLetter !== value) {
          return c;
        }
        return {
          ...c,
          classes: c.classes.map((bc) => classWithNewValueMap.get(bc.id) ?? bc),
        };
      }),
    );
  };

  const handleClassNameDifferChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    onChange(event);
    if (event.target.value !== YES) {
      // reset class name mappings if user selects "No"
      const resetCoveragesColumns = formState.coveragesColumns.map((cc) => ({
        ...cc,
        classes: cc.classes.map((c) => ({
          id: c.id,
          name: c.name,
          identifier: c.identifier,
        })),
      }));
      updateFormStateValue("coveragesColumns", resetCoveragesColumns);
    }
  };

  return (
    <>
      <CensusTitle title="Class" />
      <Box px={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                Coverage
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TitleWithInfo>Column Letter</TitleWithInfo>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  Indicate the column within the census file that illustrates
                  the classes for each coverage
                  <Required />
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              // Remove bottom border from all cells in the body (removes internal row dividers)
              "& .MuiTableCell-root": {
                borderBottom: "none",
              },
              // Add padding to first row
              "& .MuiTableRow-root:first-of-type .MuiTableCell-root": {
                paddingTop: 3,
              },
              // Add bottom border and padding to the last row
              "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                paddingBottom: 3,
              },
            }}
          >
            {formState.coveragesColumns.map((coverage) => (
              <TableRow key={coverage.coverageType}>
                <TableCell>{COVERAGE_LABELS[coverage.coverageType]}</TableCell>
                <TableCell>
                  <TextField
                    required
                    label="Column Letter"
                    size="sm"
                    value={coverage.columnLetter}
                    onChange={(e) =>
                      handleColumnChange(coverage, e.target.value)
                    }
                    disabled={isLoading}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box mt={3}>
          <CensusRadio
            name="classNamesDiffer"
            label="Do the class names on the census file differ from the class manager?"
            onChange={handleClassNameDifferChange}
            options={YES_NO_OPTIONS}
            value={formState.classNamesDiffer}
            disabled={isLoading}
          />
        </Box>
      </Box>
      {formState.classNamesDiffer === YES ? (
        <ClassNamesDiffer
          isLoading={isLoading}
          formState={formState}
          updateFormStateValue={updateFormStateValue}
        />
      ) : null}
    </>
  );
}

type ClassNamesDifferProps = Pick<
  Props,
  "formState" | "updateFormStateValue"
> & {
  isLoading: boolean;
};

const ClassNamesDiffer = ({
  isLoading,
  formState,
  updateFormStateValue,
}: ClassNamesDifferProps): React.JSX.Element => {
  const coverageClassesByColumn = useMemo(
    () =>
      formState.coveragesColumns
        .filter((cc) => cc.columnLetter)
        .reduce<Record<string, BenefitClass[]>>((acc, coverageColumn) => {
          const currentCoverage = acc[coverageColumn.columnLetter] ?? [];
          const uniqueClasses = new Map<string, BenefitClass>(
            currentCoverage.map((c) => [c.id, c]),
          );

          coverageColumn.classes.forEach((benefitClass) => {
            const currentClass =
              uniqueClasses.get(benefitClass.id) ?? benefitClass;
            uniqueClasses.set(benefitClass.id, currentClass);
          });

          acc[coverageColumn.columnLetter] = Array.from(uniqueClasses.values());
          return acc;
        }, {}),
    [formState.coveragesColumns],
  );

  const handleClassNameMappingChange = useMemo(
    () =>
      debounce(
        (
          formState: FormState,
          columnLetter: string,
          classId: string,
          newName: string,
        ): void => {
          const newCoveragesColumns = formState.coveragesColumns.map(
            (coverageColumn) => {
              if (coverageColumn.columnLetter === columnLetter) {
                const updatedClasses = coverageColumn.classes.map((c) =>
                  c.id === classId ? { ...c, newName } : c,
                );
                return { ...coverageColumn, classes: updatedClasses };
              }
              return coverageColumn;
            },
          );
          updateFormStateValue("coveragesColumns", newCoveragesColumns);
        },
        300,
      ),
    [updateFormStateValue],
  );

  const coverageClassesByColumnEntries = Object.entries(
    coverageClassesByColumn,
  );

  if (coverageClassesByColumnEntries.length === 0) {
    return null;
  }

  return (
    <Box>
      {coverageClassesByColumnEntries.map(([columnLetter, classes]) => (
        <Box key={columnLetter}>
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead
              sx={{
                "& .MuiTableCell-root": {
                  pb: 2,
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                  <Header
                    title="Class Column"
                    subtitle={`Column ${columnLetter}`}
                    displayInfo={false}
                  />
                </TableCell>
                <TableCell>
                  <Header
                    title="Census Class Name"
                    subtitle="Add the Class Name as it appears on the census"
                    required
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& .MuiTableRow-root:first-child .MuiTableCell-root": {
                  pt: 3,
                },
                "& .MuiTableCell-root": {
                  borderBottom: "none",
                },
              }}
            >
              {classes.map((classItem) => (
                <TableRow key={`${columnLetter}-${classItem.id}`}>
                  <TableCell sx={{ width: 200 }}>{classItem.name}</TableCell>
                  <TableCell>
                    <TextField
                      size="sm"
                      fullWidth
                      placeholder="Census class name"
                      onChange={(e) =>
                        handleClassNameMappingChange(
                          formState,
                          columnLetter,
                          classItem.id,
                          e.target.value,
                        )
                      }
                      disabled={isLoading}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
};

function Header({
  title,
  subtitle,
  required,
  displayInfo,
}: {
  title: string;
  displayInfo?: boolean;
  subtitle?: string;
  required?: boolean;
}): React.JSX.Element {
  return (
    <>
      <TitleWithInfo mb={1} displayInfo={displayInfo}>
        {title}
      </TitleWithInfo>
      <Typography variant="body-large" color="textSecondary" mb={1}>
        <strong>{subtitle}</strong>
      </Typography>
      {required ? <Required /> : null}
    </>
  );
}

function Required(): React.JSX.Element {
  return (
    <Typography pl={0.2} color="danger" component="span">
      *
    </Typography>
  );
}

const TitleWithInfo = ({
  children,
  displayInfo = true,
  ...rest
}: PropsWithChildren<
  TypographyProps & { displayInfo?: boolean }
>): React.JSX.Element => (
  <Typography
    display="flex"
    alignItems="center"
    variant="body-medium"
    {...rest}
  >
    <strong>{children}</strong>
    {displayInfo ? (
      <InfoIcon
        fontSize="small"
        aria-hidden={true}
        focusable={false}
        sx={{ ml: 1 }}
      />
    ) : null}
  </Typography>
);

export const MultipleOptionSection = ({
  formState,
  isLoading,
  updateFormStateValue,
}: Pick<
  PropsWithLoading,
  "formState" | "isLoading" | "updateFormStateValue"
>): React.JSX.Element | null => {
  const selectedCoveragesWithMultipleOption = ["dental", "vision"]
    .map((type) => {
      const coverage = formState.selectedCoverages[type];
      if (!coverage) {
        return null;
      }

      const coverageOption = coverage.coverageOption;
      if (!coverageOption) {
        return null;
      }

      const options =
        COVERAGE_QUESTIONNAIRE_OPTIONS[type]?.[coverageOption.value];

      if (!options) {
        return null;
      }
      return { coverage, options, type };
    })
    .filter(Boolean);

  if (selectedCoveragesWithMultipleOption.length === 0) {
    return null;
  }

  const handlePlanNameChange = (
    coverage: Coverage,
    planKey: string,
    planName: string,
  ): void => {
    const coverageCopy = cloneDeep(coverage);
    set(coverageCopy, `coverageOption.plans.${planKey}`, planName);
    const newSelectedCoverages = {
      ...formState.selectedCoverages,
      [coverage.coverage]: coverageCopy,
    };
    updateFormStateValue("selectedCoverages", newSelectedCoverages);
  };

  return (
    <>
      <CensusTitle title="Multiple Option Coverage" mt={3} />
      <Box px={2} mb={3}>
        <Typography variant="body-medium" color="textSecondary" mb={1}>
          Multiple plan options for a LOC have been detected. Please give names
          to the individual plans.
          <Required />
        </Typography>

        {selectedCoveragesWithMultipleOption.map(
          ({ coverage, options, type }) => (
            <Table size="small" key={type}>
              <TableHead
                sx={{
                  "& .MuiTableCell-root": {
                    py: 2,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Coverage</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Plan Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  "& .MuiTableRow-root:first-child .MuiTableCell-root": {
                    pt: 3,
                  },
                  "& .MuiTableCell-root": {
                    borderBottom: "none",
                  },
                }}
              >
                {options.map((plan) => (
                  <TableRow key={plan.value}>
                    <TableCell sx={{ maxWidth: 50 }}>{plan.label}</TableCell>
                    <TableCell>
                      <TextField
                        size="sm"
                        fullWidth
                        placeholder="Plan Name"
                        value={
                          coverage.coverageOption?.plans?.[plan.value] ?? ""
                        }
                        onChange={(e) =>
                          handlePlanNameChange(
                            coverage,
                            plan.value,
                            e.target.value,
                          )
                        }
                        disabled={isLoading}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ),
        )}
      </Box>
    </>
  );
};

export const COVERAGE_QUESTIONNAIRE_OPTIONS: Record<
  string,
  Record<string, { label: string; value: string }[]>
> = {
  dental: {
    dualOption: [
      {
        label: "PPO Low",
        value: "low",
      },
      {
        label: "PPO High",
        value: "high",
      },
    ],
    hmoPpo: [
      {
        label: "HMO",
        value: "low",
      },
      {
        label: "Single PPO",
        value: "high",
      },
    ],
    tripleOption: [
      {
        label: "Dental PPO Low",
        value: "low",
      },
      {
        label: "Dental PPO Medium",
        value: "medium",
      },
      {
        label: "Dental PPO High",
        value: "high",
      },
    ],
  },
  vision: {
    dualOption: [
      { label: "Vision Low", value: "low" },
      { label: "Vision High", value: "high" },
    ],
    tripleOption: [
      { label: "Vision Low", value: "low" },
      { label: "Vision Medium", value: "medium" },
      { label: "Vision High", value: "high" },
    ],
  },
};

export { CensusTabCensusForm };
